import { Layers3 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { integer, money, percent, signedPercent, signedPoints } from '../lib/format'
import type { PublicDemoBundle } from '../types'
import { useI18n, type Locale } from '../i18n'

export type OverviewMetric = PublicDemoBundle['overview']['metrics'][number]

export function BrandLockup({ to = '/' }: { to?: string }) {
  const { copy, path } = useI18n()
  return (
    <Link to={path(to)} className="brand-link" aria-label={copy.common.brandHome}>
      <span className="brand-mark"><Layers3 size={17} /></span>
      <span><strong>Commerce</strong> Intelligence</span>
    </Link>
  )
}

export function SectionHeading({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return <header className="section-heading"><span>{eyebrow}</span><h2>{title}</h2><p>{copy}</p></header>
}

export function formatMetricValue(metric: OverviewMetric, locale: Locale = 'en') {
  if (['net_sales_after_returns', 'contribution_profit', 'gross_profit'].includes(metric.metric_id)) return money(metric.current_value, 1, locale)
  if (metric.change_semantics === 'PERCENTAGE_POINT') return percent(metric.current_value)
  return integer(metric.current_value, locale)
}

export function formatMetricPrevious(metric: OverviewMetric, locale: Locale = 'en') {
  if (['net_sales_after_returns', 'contribution_profit', 'gross_profit'].includes(metric.metric_id)) return money(metric.previous_value, 1, locale)
  if (metric.change_semantics === 'PERCENTAGE_POINT') return percent(metric.previous_value)
  return integer(metric.previous_value, locale)
}

export function formatMetricChange(metric: OverviewMetric, locale: Locale = 'en') {
  return metric.change_semantics === 'PERCENTAGE_POINT' ? signedPoints(metric.absolute_change, locale) : signedPercent(metric.relative_change)
}

export function metricIsFavorable(metric: OverviewMetric) {
  return metric.metric_id === 'return_rate' ? metric.absolute_change <= 0 : metric.absolute_change >= 0
}

export function MetricCard({ metric, compact = false }: { metric: OverviewMetric; compact?: boolean }) {
  const { copy, locale } = useI18n()
  const favorable = metricIsFavorable(metric)
  return (
    <article className={`metric-card${compact ? ' compact' : ''}`}>
      <span>{copy.metricLabels[metric.metric_id as keyof typeof copy.metricLabels] ?? metric.display_name}</span>
      <strong>{formatMetricValue(metric, locale)}</strong>
      <div className="metric-context">
        <b className={favorable ? 'metric-change favorable' : 'metric-change headwind'}>{formatMetricChange(metric, locale)}</b>
        <small>{copy.common.previous28d}</small>
      </div>
    </article>
  )
}
